import { Link } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bookmark, BookmarkCheck, DollarSign, ExternalLink, Bell, BarChart3, Users, TrendingUp } from "lucide-react";
import { PolymarketEvent } from "@/services/polymarketEventService";
import { CountdownTimer } from "./CountdownTimer";
import { cdnImage } from "@/lib/images";
import T from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface EventCardProps {
  event: PolymarketEvent;
  isBookmarked?: boolean;
  onBookmarkToggle?: (eventId: string) => void;
  onSetAlert?: (event: PolymarketEvent) => void;
  showBookmarkButton?: boolean;
}

export function EventCard({ 
  event, 
  isBookmarked = false, 
  onBookmarkToggle,
  onSetAlert,
  showBookmarkButton = true 
}: EventCardProps) {
  const t = useT();
  const formatVolume = (volume: number) => {
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(2)}M`;
    }
    if (volume >= 1000) {
      return `$${(volume / 1000).toFixed(2)}K`;
    }
    return `$${volume.toFixed(0)}`;
  };

  const volume24h = event.volume * 0.03; // Approximate 24h as 3% of total (fallback)

  return (
    <Card className="hover:shadow-lg transition-all duration-200 overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm">
      <CardContent className="p-4 md:p-5">
        <div className="flex gap-4">
          {/* Image */}
          {event.image && (
            <div className="shrink-0">
              <img
                src={cdnImage(event.image, { width: 80, height: 80, fit: "cover" })}
                alt={event.title}
                width={80}
                height={80}
                loading="lazy"
                decoding="async"
                className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <Link to={`/polymarket/${event.id}`} className="block flex-1 min-w-0 group">
                <h3 className="text-lg md:text-xl font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                  {event.title}
                </h3>
              </Link>
              
              {/* Countdown Timer */}
              {event.end_date && (
                <div className="shrink-0 hidden sm:block">
                  <CountdownTimer endDate={event.end_date} />
                </div>
              )}
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <DollarSign className="h-4 w-4 text-primary" />
                <span className="font-medium text-foreground">{formatVolume(event.volume)}</span>
                <span><T k="auto.total-vol-8020" /></span>
              </span>
              <span className="flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-success" />
                <span className="font-medium text-foreground">{formatVolume(volume24h)}</span>
                <span><T k="auto.24h-vol-0d59" /></span>
              </span>
              <span className="flex items-center gap-1.5">
                <BarChart3 className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-foreground">{formatVolume(event.liquidity || event.volume * 0.05)}</span>
                <span><T k="auto.liquidity-ced4" /></span>
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-foreground">{event.markets_count}</span>
                <span><T k="auto.markets-52c3" /></span>
              </span>
            </div>

            {/* Mobile Countdown */}
            {event.end_date && (
              <div className="sm:hidden mb-3">
                <CountdownTimer endDate={event.end_date} />
              </div>
            )}

            {/* Actions Row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {event.category && (
                  <Badge variant="secondary" className="text-xs">
                    {event.category}
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {showBookmarkButton && onSetAlert && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onSetAlert(event)}
                    title={t("auto.set-price-alert-24fa")}
                  >
                    <Bell className="h-4 w-4" />
                  </Button>
                )}
                {showBookmarkButton && onBookmarkToggle && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onBookmarkToggle(event.event_id)}
                  >
                    {isBookmarked ? (
                      <BookmarkCheck className="h-4 w-4 text-primary" />
                    ) : (
                      <Bookmark className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <a
                  href={`https://polymarket.com/event/${event.slug || event.event_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="sm" className="bg-success hover:bg-success/90 text-success-foreground gap-1.5">
                    <T k="auto.trade-on-polymarket-c80a" />
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
